import React, { useEffect, useState } from 'react';
import {
    AreaChart,
    Area,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    PieChart,
    Pie,
    Cell,
    Legend,
    ResponsiveContainer,
} from 'recharts';
import { Container, Paper } from '@mui/material';
import { ButtonGroup, Button, } from '@material-ui/core';
import Text from '../../../components/Typography/Text';
import { listDashboardBalance } from '../../../services/BalanceService';
import { getPositionAccountAllocation } from '../../../services/PositionService';
import { getSystemDate } from '../../../services/ProfileService';
import { formatCurrency, formatPbDate } from '../../../lib/fmt2';

const ChartSummary = () => {
    const [data, setData] = useState([]);
    const [assetData, setAssetData] = useState([]);
    const [sectorData, setSectorData] = useState([]);
    const [maxBalance, setMaxBalance] = useState(0);
    const [minBalance, setMinBalance] = useState(0);
    const [range, setRange] = useState(1);
    const [period, setPeriod] = useState('');

    const assetColors = ['#5a7ea9', '#5f8a62', '#a8595a', '#c7945e', '#8c6f9b', '#b6c4cf'];

    const CustomLegend = ({ data }) => {
        return (
            <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', flexWrap: 'wrap' }}>
                {data.map((entry, index) => (
                    <div key={`legend-${index}`} style={{ display: 'flex', alignItems: 'center', marginRight: 10, marginBottom: 5 }}>
                        <div style={{ width: 15, height: 15, backgroundColor: assetColors[index % assetColors.length], marginRight: 5 }}></div>
                        <div>{`${entry.name} (${entry.value}%)`}</div>
                    </div>
                ))}
            </div>
        );
    };

    const CustomTooltip = ({ active, payload, label }) => {
        if (active && payload && payload.length) {
            const description = payload[0].payload.description;
            const sector = payload[0].payload.sector;
            return (
                <div className="card" style={{ backgroundColor: 'rgba(255, 255, 255, 0.8)' }}>
                    <p>{`code: ${label}`}</p>
                    <p>{`description: ${description}`}</p>
                    <p>{`percentage: ${sector}%`}</p>
                </div>
            );
        }
        return null;
    };

    useEffect(() => {
        const fetchData = async () => {
            try {
                const systemDate = await getSystemDate();

                const currentDate = new Date(systemDate);

                currentDate.setMonth(currentDate.getMonth() - range);
                const fromDate = currentDate.toISOString().split('T')[0];

                const searchData = {
                    fromDate: fromDate,
                    toDate: systemDate,
                    period: period,
                };

                const { dashboardBalancesList } = await listDashboardBalance(searchData);

                const maxValue = dashboardBalancesList.reduce((max, item) => {
                    return Math.max(max, item.tdCashBalance);
                }, -Infinity);

                const minValue = dashboardBalancesList.reduce((min, item) => {
                    return Math.min(min, item.tdCashBalance);
                }, Infinity);

                const formattedData = dashboardBalancesList.map(item => ({
                    date: formatPbDate(item.date),
                    balance: item.tdCashBalance
                }));

                setMinBalance(minValue);
                setMaxBalance(maxValue);
                setData(formattedData);

            } catch (error) {
                console.error("Error fetching data:", error);
            }
        };

        fetchData();
        // eslint-disable-next-line
    }, [range]);

    useEffect(() => {
        const fetchData = async () => {
            const { positionAccountAllocationsList } = await getPositionAccountAllocation('Asset');

            const formattedAssetData = positionAccountAllocationsList.map(item => ({
                name: item.code,
                value: parseFloat(item.percentage),
            }));
            setAssetData(formattedAssetData);
        }
        fetchData();
    }, []);

    useEffect(() => {
        const fetchData = async () => {
            const { positionAccountAllocationsList } = await getPositionAccountAllocation('Sector');

            const formattedSectorData = positionAccountAllocationsList.map(item => ({
                name: item.code,
                description: item.description,
                sector: parseFloat(item.percentage),
            }));

            setSectorData(formattedSectorData);
        }
        fetchData();
    }, []);

    return (
        <div>
            <ResponsiveContainer width="100%" height={410}>
                <AreaChart data={data}>
                    <XAxis dataKey="date" />
                    <YAxis domain={[minBalance, maxBalance]} tickFormatter={value => formatCurrency(value)} width={120} />
                    <Tooltip formatter={(value) => formatCurrency(value)} />
                    <CartesianGrid dataKey="balance" strokeDasharray="1 1" />
                    <Area type="monotone" dataKey="balance" stroke="#8884d8" fill="#8884d8" />
                </AreaChart>
            </ResponsiveContainer>

            <div className="grd-row">
                <div className="grd-cell" style={{ alignItems: 'center' }}>
                    <Text mb={10} variant="h3" label="Balance Summary" />
                    <ButtonGroup disableElevation color="primary">
                        <Button variant={range === 1 ? 'contained' : 'outlined'}
                            onClick={() => {
                                setRange(1);
                                setPeriod('');
                            }}> 1M</Button>
                        <Button variant={range === 3 ? 'contained' : 'outlined'}
                            onClick={() => {
                                setRange(3);
                                setPeriod('');
                            }}>3M</Button>
                        <Button
                            variant={range === 6 ? 'contained' : 'outlined'}
                            onClick={() => {
                                setRange(6);
                                setPeriod('Weekly');
                            }}
                        >
                            6M
                        </Button>
                        <Button variant={range === 9 ? 'contained' : 'outlined'}
                            onClick={() => {
                                setRange(9);
                                setPeriod('Weekly');
                            }}>9M</Button>
                        <Button variant={range === 12 ? 'contained' : 'outlined'}
                            onClick={() => {
                                setRange(12);
                                setPeriod('Monthly');
                            }}>1Y</Button>
                        <Button variant={range === 36 ? 'contained' : 'outlined'}
                            onClick={() => {
                                setRange(36);
                                setPeriod('Monthly');
                            }}>3Y</Button>
                        <Button variant={range === 60 ? 'contained' : 'outlined'}
                            onClick={() => {
                                setRange(60);
                                setPeriod('Yearly');
                            }}>5Y</Button>
                        <Button variant={range === 84 ? 'contained' : 'outlined'}
                            onClick={() => {
                                setRange(84);
                                setPeriod('Yearly');
                            }}>7Y</Button>
                        <Button variant={range === 108 ? 'contained' : 'outlined'}
                            onClick={() => {
                                setRange(108);
                                setPeriod('Yearly');
                            }}>9Y</Button>
                        <Button variant={range === 0 ? 'contained' : 'outlined'}
                            onClick={() => {
                                setRange(0);
                                setPeriod('All Time');
                            }}>All Time</Button>
                    </ButtonGroup>
                </div>
            </div>
            <div className="grd-row" style={{ marginTop: 80 }}>
                <div className="grd-cell" style={{ alignItems: 'center' }}>
                    <div className="card" style={{ width: '100%', height: 400 }}>
                        <Text mb={10} variant="h3" label="Asset" />
                        {assetData.length > 0 ? (
                            <ResponsiveContainer width="100%" height="100%">
                                <PieChart width={400} height={400}>
                                    <Pie
                                        data={assetData}
                                        cx="50%"
                                        cy="50%"
                                        outerRadius={120}
                                        labelLine={false}
                                        fill="#8884d8"
                                        dataKey="value"
                                    >
                                        {assetData.map((entry, index) => (
                                            <Cell key={`cell-${index}`} fill={assetColors[index % assetColors.length]} />
                                        ))}
                                    </Pie>
                                    <Tooltip
                                        formatter={(value, name, props) => [`${value}%`, props.payload.name]}
                                    />
                                    <Legend content={<CustomLegend data={assetData} />} />
                                </PieChart>
                            </ResponsiveContainer>
                        ) : (
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '30vh' }}>
                                <Text
                                    variant="body1"
                                    label={'Sorry, no data found'}
                                />
                            </div>
                        )}
                    </div>
                </div>
                <div className="grd-cell" style={{ alignItems: 'center' }}>
                    <div className="card" style={{ width: '100%', height: 400 }}>
                        <Text mb={10} variant="h3" label="Sector" />
                        {sectorData.length > 0 ? (
                            <ResponsiveContainer width="100%" >
                                <AreaChart data={sectorData}>
                                    <XAxis dataKey="name" />
                                    <YAxis tickFormatter={value => value + '%'} />
                                    <Tooltip content={<CustomTooltip />} />
                                    <CartesianGrid dataKey="sector" strokeDasharray="1 1" />
                                    <Area type="monotone" dataKey="sector" stroke="#ff7f0e80" fill="#ff7f0e80" />
                                </AreaChart>
                            </ResponsiveContainer>
                        ) : (
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '30vh' }}>
                                <Text
                                    variant="body1"
                                    label={'Sorry, no data found'}
                                />
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ChartSummary;
